/**
 * @fileoverview Root-level error boundary for catastrophic failures.
 * Provides a full-page error UI when the entire app crashes.
 */

import * as React from 'react';
import { Alert, Box, Container, Typography } from '@mui/material';
import { ErrorOutlined, Home, Refresh } from '@mui/icons-material';
import { useRouteError } from 'react-router';
import { Button } from '@/shared/components/Button';
import { errorDetailsSx, parseRouteError } from '../utils/errorHelpers';

export function RootErrorBoundary(): React.JSX.Element {
  const error = useRouteError();
  const { message, details } = parseRouteError(error, 'An unexpected error occurred');

  return (
    <Container maxWidth='md' sx={{ py: 8 }}>
      <Alert
        severity='error'
        icon={<ErrorOutlined fontSize='large' />}
        sx={{
          alignItems: 'center',
          '& .MuiAlert-icon': { fontSize: '3rem' },
        }}
      >
        <Typography variant='h5' gutterBottom>
          Application Error
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
            mb: 2
          }}>
          {message}
        </Typography>

        {details && process.env.NODE_ENV === 'development' && (
          <Box component='pre' sx={{ ...errorDetailsSx, maxHeight: 300 }}>
            {details}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant='contained'
            startIcon={<Home />}
            onClick={() => window.location.href = '/'}
          >
            Go to Home
          </Button>
          <Button
            variant='outlined'
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Reload Page
          </Button>
        </Box>
      </Alert>
    </Container>
  );
}

